import { FC, memo, useState, useCallback } from "react";

import { InfoCircleFill } from "@hl/base-components/lib/assets/icons.generated";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { Text, Stack, Grid, HoverCard, Anchor, Group } from "@mantine/core";

import { NftContractStandard } from "apollo/graphql.generated";

import { SelectBox } from "../../sale/SelectBox";

type Props = {
  value?: NftContractStandard;
  onChange: (value: NftContractStandard) => void;
};

const TokenStandardSelect: FC<Props> = ({ onChange, value = undefined }) => {
  const [tokenStandard, setTokenStandard] = useState<NftContractStandard>(
    value || NftContractStandard.ERC721
  );

  const handleStandardChange = useCallback(
    (standard: NftContractStandard) => {
      setTokenStandard(standard);
      onChange?.(standard);
    },
    [onChange]
  );

  const url =
    "https://support.highlight.xyz/knowledge-base/for-creators/learn-the-creation-flows/comparing-erc-721-to-erc-1155-tokens";

  return (
    <Stack spacing={8}>
      <Group spacing={4} align="center">
        <Text component="label" size="sm">
          Token standard
        </Text>
        <HoverCard shadow="md">
          <HoverCard.Target>
            <Anchor href={url} target="_blank" h={17}>
              <InfoCircleFill width={17} height={17} stroke={"#000000"} />
            </Anchor>
          </HoverCard.Target>
          <HoverCard.Dropdown>
            <Text size="sm">
              Learn more about different{" "}
              <Anchor size="sm" fw={WEIGHT_BOLD} href={url} target="_blank">
                token standards
              </Anchor>
              .
            </Text>
          </HoverCard.Dropdown>
        </HoverCard>
      </Group>
      <Grid>
        <Grid.Col span={6}>
          <SelectBox
            checked={tokenStandard === NftContractStandard.ERC721}
            label="ERC 721"
            description="The original NFT standard that allows you to create unique, one-of-a-kind digital assets, perfect for individual artworks or collectibles"
            onClick={() => handleStandardChange(NftContractStandard.ERC721)}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <SelectBox
            checked={tokenStandard === NftContractStandard.ERC1155}
            label="ERC 1155"
            description="A newer standard that helps create limited-edition NFTs with the same artwork, appearing batched together in your wallet and on marketplaces"
            onClick={() => handleStandardChange(NftContractStandard.ERC1155)}
          />
        </Grid.Col>
      </Grid>
    </Stack>
  );
};

export default memo(TokenStandardSelect);
